import {ref} from 'vue'
import {defineStore} from 'pinia'
import {config} from "@/env-config";
import {decodeJwt} from "jose";
import {AuthSpaClient, type ITokenExchangeResult} from "@dbg-health/auth-sdk";
import {buildState, parseState, splitOnWhitespace} from "@/utils";

// --- Config values ---
const CLIENT_ID = '76ce7d4f-ed55-47ca-9f84-9bbb65543a6b';
const VALUE_API_RESOURCE = config.apiHost;
const REDIRECT_URI = `${window.location.origin}/auth/callback`; // Must match registered redirect URI at auth server
const SCOPES = 'openid read:groups read:stores read:roles read:user write:groups read:permissions write:permissions write:assignments read:datasets write:datasets delete:datasets read:config write:config';

export interface ILoginProps {
    targetUrl?: string;
    prompt?: string;
}

export const useAuthStore = defineStore('auth', () => {
    const isAuthLoading = ref(false)
    const isLoggedIn = ref(false)
    const accessToken = ref<string | undefined>('')
    const idToken = ref<string | undefined>('')
    const authBase = ref<string>(config.authHost)

    const authError = ref<string | undefined>('');
    const authErrorDescription = ref<string | undefined>('');
    const authRoles = ref<string[]>([]);

    const refreshToken = ref<string | undefined>(undefined);

    const authSpaClient = new AuthSpaClient({
        clientId: CLIENT_ID,
        issuerUri: config.authHost,
    });

    function setTokens(codeExchangeResult: ITokenExchangeResult) {
        if (!codeExchangeResult.id_token) {
            throw new Error('ID token is missing in the token exchange result');
        }

        if (!codeExchangeResult.access_token) {
            throw new Error('Access token is missing in the token exchange result');
        }

        if (!codeExchangeResult.refresh_token) {
            throw new Error('Refresh token is missing in the token exchange result');
        }

        refreshToken.value = codeExchangeResult.refresh_token;
        accessToken.value = codeExchangeResult.access_token;
        idToken.value = codeExchangeResult.id_token;

        const decodedIdToken = decodeJwt(codeExchangeResult.id_token!);
        authRoles.value = splitOnWhitespace(decodedIdToken.roles as string);
        isLoggedIn.value = true;

        if ((window as any).clarity) {
            (window as any).clarity("identify", decodedIdToken.sub);
        }
    }

    function clearTokens() {
        refreshToken.value = undefined;
        accessToken.value = '';
        idToken.value = '';
        isLoggedIn.value = false;
    }

    async function doTokenRefresh(): Promise<boolean> {

        if (!refreshToken.value) {
            clearTokens();
            return false;
        }

        const result = await authSpaClient.refreshToken({
            scopes: SCOPES,
            refreshToken: refreshToken.value
        });
        if (result) {
            setTokens(result);
            return true;
        } else {
            clearTokens();
            return false;
        }
    }

    async function login(props?: ILoginProps): Promise<void> {
        const state = buildState({
            targetUrl: props?.targetUrl || '/about'
        });
        try {
            isAuthLoading.value = true;
            // redirect to the auth server for login
            await authSpaClient.authorize({
                redirectUri: REDIRECT_URI,
                scopes: SCOPES,
                resource: VALUE_API_RESOURCE,
                state,
                prompt: props?.prompt
            }).then(_ => {});
        } catch (e: any) {
            console.error('Error during login:', e);
            authErrorDescription.value = e.message || 'An error occurred during login';
            isLoggedIn.value = false;
            clearTokens();
            throw e;
        } finally {
            isAuthLoading.value = false
        }
    }

    async function logout(): Promise<void> {
        isAuthLoading.value = true;

        try {
            isLoggedIn.value = false;
            const idTokenHint = idToken.value;
            clearTokens();

            // Calculate post-logout redirect URI
            const currentOrigin = window.location.origin;
            const postLogoutRedirectUri = `${currentOrigin}/welcome`;

            await authSpaClient.endSession({
                idTokenHint,
                postLogoutRedirectUri
            });
        } catch (error) {
            console.error('Error during logout:', error);
        } finally {
            isAuthLoading.value = false;
        }
    }

    async function silentLogin(): Promise<boolean> {
        isAuthLoading.value = true;

        try {
            isLoggedIn.value = false;

            const result = await authSpaClient.authorizeSilently({
                resource: VALUE_API_RESOURCE,
                scopes: SCOPES,
            });
            if (result) {
                setTokens(result);
                isLoggedIn.value = true;
                return true;
            } else {
                clearTokens();
                return false;
            }
        } catch (e: any) {
            console.error('Error during silent login:', e);
            authErrorDescription.value = e.message || 'An error occurred during silent login';
            clearTokens();
            return false;
        } finally {
            isAuthLoading.value = false
        }
    }

    async function handleCallback(code: string, rawState: string): Promise<string> {
        const result = await authSpaClient.handleCallback({
            code,
            redirectUri: REDIRECT_URI,
            resource: VALUE_API_RESOURCE,
        });

        if (result) {
            setTokens(result);
            const parsedState = parseState(rawState);
            return parsedState.targetUrl || '/about';
        } else {
            clearTokens();
            throw new Error('Failed to handle OAuth callback');
        }
    }

    function userInfo() {
        if (!isLoggedIn.value) {
            return null;
        }

        const decodedIdToken = decodeJwt(idToken.value!);

        return {
            username: decodedIdToken.sub,
            email: decodedIdToken.email,
            firstName: decodedIdToken.given_name,
            lastName: decodedIdToken.family_name
        };
    }

    function hasRole(role: string): boolean {
        if (!role) {
            return false;
        }
        return authRoles.value.includes(role);
    }

    async function refreshOrLogin(): Promise<void> {
        if (accessToken.value) {
            if (authSpaClient.isTokenExpired(accessToken.value)) {
                const refreshed = await doTokenRefresh();
                if (refreshed) {
                    return;
                }
            } else {
                // Token is still valid, no need to refresh
                return;
            }
        }
        // fire and forget.  After login, try to land back on the page we're on after login
        return login({targetUrl: window.location.pathname.replace("/ui/", "/")});
    }

    return {
        isAuthLoading,
        isLoggedIn,
        accessToken,
        idToken,
        authBase,
        authError,
        authErrorDescription,
        authRoles,

        silentLogin,
        login,
        logout,
        handleCallback,
        refreshOrLogin
    }
})
